import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { cn } from '@/utils/cn';

import { Listbox } from './listbox';
import type { PopoverPortalTarget } from './popover-portal';

export interface CalendarProps {
  value?: string | null;
  onChange?: (date: string) => void;
  min?: string;
  max?: string;
  popoverPortal?: PopoverPortalTarget;
  monthLabel?: string;
  yearLabel?: string;
  previousMonthLabel?: string;
  nextMonthLabel?: string;
  locale?: string;
  className?: string;
}

const toYmd = (date: Date): string => date.toISOString().slice(0, 10);

const fromYmd = (ymd: string): Date => {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
};

const utcToday = (): string => toYmd(new Date());

const firstDayOfWeek = (locale: string): number => {
  try {
    const info = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    return info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay ?? 1;
  } catch {
    return 1;
  }
};

const startOfMonth = (ymd: string): Date => {
  const date = fromYmd(ymd);
  date.setUTCDate(1);
  return date;
};

const buildGrid = (monthStart: Date, weekStart: number): Date[] => {
  const weekday = monthStart.getUTCDay() === 0 ? 7 : monthStart.getUTCDay();
  const offset = (weekday - weekStart + 7) % 7;
  const start = new Date(monthStart);
  start.setUTCDate(start.getUTCDate() - offset);
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return day;
  });
};

export const Calendar = ({
  value = null,
  onChange,
  min,
  max,
  popoverPortal = 'body',
  monthLabel,
  yearLabel,
  previousMonthLabel,
  nextMonthLabel,
  locale = 'en',
  className,
}: CalendarProps) => {
  const today = utcToday();
  const [cursor, setCursor] = useState(() => startOfMonth(value ?? today));

  const weekStart = useMemo(() => firstDayOfWeek(locale), [locale]);
  const days = useMemo(() => buildGrid(cursor, weekStart), [cursor, weekStart]);

  const weekdayNames = useMemo(() => {
    const monday = new Date(Date.UTC(2021, 0, 4));
    const names = Array.from({ length: 7 }, (_, index) => {
      const day = new Date(monday);
      day.setUTCDate(monday.getUTCDate() + index);
      return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(day);
    });
    return [...names.slice(weekStart - 1), ...names.slice(0, weekStart - 1)];
  }, [locale, weekStart]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
          new Date(Date.UTC(2021, index, 1)),
        ),
      })),
    [locale],
  );

  const yearOptions = useMemo(() => {
    const centre = cursor.getUTCFullYear();
    return Array.from({ length: 121 }, (_, offset) => centre - 100 + offset).map((year) => ({
      value: year,
      label: String(year),
    }));
  }, [cursor]);

  const isOutOfRange = (date: string): boolean =>
    (min !== undefined && date < min) || (max !== undefined && date > max);

  const shiftMonth = (delta: number) => {
    const next = new Date(cursor);
    next.setUTCMonth(cursor.getUTCMonth() + delta);
    setCursor(next);
  };

  return (
    <div className={cn('w-72 select-none p-3', className)}>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          aria-label={previousMonthLabel}
          onClick={() => shiftMonth(-1)}
          className="shrink-0 rounded-md p-1.5 text-fg-secondary hover:bg-surface-sunken hover:text-fg"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 gap-1.5">
          <Listbox<number>
            value={cursor.getUTCMonth() + 1}
            onChange={(month) => {
              if (!month) return;
              const next = new Date(cursor);
              next.setUTCMonth(month - 1);
              setCursor(next);
            }}
            options={monthOptions}
            size="sm"
            className="min-w-0 flex-1"
            popoverPortal={popoverPortal}
            aria-label={monthLabel}
          />
          <Listbox<number>
            value={cursor.getUTCFullYear()}
            onChange={(year) => {
              if (!year) return;
              const next = new Date(cursor);
              next.setUTCFullYear(year);
              setCursor(next);
            }}
            options={yearOptions}
            searchable
            size="sm"
            className="w-20 shrink-0"
            popoverPortal={popoverPortal}
            aria-label={yearLabel}
          />
        </div>

        <button
          type="button"
          aria-label={nextMonthLabel}
          onClick={() => shiftMonth(1)}
          className="shrink-0 rounded-md p-1.5 text-fg-secondary hover:bg-surface-sunken hover:text-fg"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5" role="grid">
        {weekdayNames.map((name) => (
          <div
            key={name}
            role="columnheader"
            className="pb-1 text-center text-overline font-semibold text-fg-tertiary uppercase"
          >
            {name.slice(0, 2)}
          </div>
        ))}

        {days.map((day) => {
          const iso = toYmd(day);
          const outsideMonth = day.getUTCMonth() !== cursor.getUTCMonth();
          const unavailable = isOutOfRange(iso);
          const selected = value === iso;
          const isToday = iso === today;

          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              disabled={unavailable}
              aria-selected={selected}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => onChange?.(iso)}
              className={cn(
                'relative flex h-9 items-center justify-center rounded-md text-body-sm transition-colors',
                outsideMonth ? 'text-fg-tertiary' : 'text-fg',
                !unavailable &&
                  !selected &&
                  (isToday ? 'hover:bg-brand/10' : 'hover:bg-surface-sunken'),
                selected && 'bg-brand font-semibold text-on-brand hover:bg-brand-hover',
                isToday &&
                  !selected &&
                  'bg-brand-subtle font-semibold text-brand ring-2 ring-inset ring-brand',
                unavailable && 'cursor-not-allowed text-fg-disabled hover:bg-transparent',
              )}
            >
              {day.getUTCDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
